import { useEffect, useState } from "react";
import { CheckIcon, CopyIcon, ExternalLinkIcon, RotateCwIcon } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "./ui/input-group";
import { copyToClipboard } from "../lib/clipboard";
import { cn } from "../lib/utils";
import type { WebhookSummary, WebhookType } from "../lib/api";

export interface WebhookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The webhook being edited. When `null` the dialog is in "create" mode
   * and shows the secret once after the caller submits.
   */
  webhook: WebhookSummary | null;
  onSubmit: (input: {
    name: string;
    url: string;
    secret: string;
    enabled: boolean;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

/**
 * WebhookDialog is the create/edit modal for a single webhook. The flow
 * is split in two: when the caller provides an existing `webhook` we
 * edit it (no secret is shown — the dashboard doesn't store it after
 * creation); when `webhook` is null we create one and, after a
 * successful submit, show the freshly-minted secret with a one-click
 * copy button.
 */
export function WebhookDialog({
  open,
  onOpenChange,
  webhook,
  onSubmit,
  busy,
  error,
}: WebhookDialogProps) {
  const isEdit = webhook != null;
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [mintedSecret, setMintedSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMintedSecret(null);
    setCopied(false);
    if (webhook) {
      setName(webhook.name);
      setUrl(webhook.url);
      setSecret("");
      setEnabled(webhook.enabled);
    } else {
      setName("");
      setUrl("");
      setSecret(generateSecret());
      setEnabled(true);
    }
  }, [open, webhook]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await onSubmit({ name: name.trim(), url: url.trim(), secret: secret.trim(), enabled });
    // The create response carries the secret; the parent forwards it
    // through `onSubmit` returning void here, so the caller is
    // expected to populate mintedSecret via a state update from the
    // dialog. The simplest path: callers render the dialog inside a
    // wrapper that owns the `mintedSecret` slot and toggles it
    // externally.
  }

  async function copySecret() {
    if (!mintedSecret) return;
    const result = await copyToClipboard(mintedSecret);
    if (result.ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onOpenChange(false);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit webhook` : `Add a Discord webhook`}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the receiver URL or toggle this webhook on and off."
              : "Paste the webhook URL from your Discord server's Integrations settings. Mushi will POST a formatted embed every time a new report arrives."}
          </DialogDescription>
        </DialogHeader>

        {mintedSecret ? (
          <Alert variant="destructive" data-testid="webhook-secret-banner">
            <AlertTitle>Copy this signing secret — it will not be shown again</AlertTitle>
            <AlertDescription>
              Anyone with this secret can forge webhook calls that look like they came from Mushi. Store it in a password manager or environment variable.
            </AlertDescription>
            <div className="mt-3 flex flex-wrap items-stretch gap-2">
              <code
                data-testid="webhook-secret"
                className="min-w-0 flex-1 break-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-xs text-foreground"
              >
                {mintedSecret}
              </code>
              <Button type="button" size="sm" onClick={copySecret} data-testid="copy-webhook-secret">
                {copied ? <><CheckIcon /> Copied</> : <><CopyIcon /> Copy secret</>}
              </Button>
            </div>
          </Alert>
        ) : (
          <form
            id="webhook-form"
            onSubmit={handleSubmit}
            data-testid="webhook-form"
            className="flex flex-col gap-4"
          >
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="webhook-name">Name</FieldLabel>
                <Input
                  id="webhook-name"
                  name="name"
                  required
                  maxLength={80}
                  placeholder="e.g. #bug-feed"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <FieldDescription>
                  A short label so you can tell multiple webhooks apart in the list.
                </FieldDescription>
              </Field>

              <Field>
                <FieldLabel htmlFor="webhook-url">Discord webhook URL</FieldLabel>
                <Input
                  id="webhook-url"
                  name="url"
                  type="url"
                  required
                  placeholder="https://discord.com/api/webhooks/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
                <FieldDescription>
                  Found in Discord under <span className="font-mono">Server settings → Integrations → Webhooks</span>.
                  <a
                    href="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="ml-1 inline-flex items-center gap-0.5 text-primary underline-offset-4 hover:underline"
                  >
                    Discord docs
                    <ExternalLinkIcon className="size-3" />
                  </a>
                </FieldDescription>
              </Field>

              <Field>
                <FieldLabel htmlFor="webhook-secret-input">Signing secret</FieldLabel>
                <InputGroup>
                  <InputGroupInput
                    id="webhook-secret-input"
                    name="secret"
                    required={!isEdit}
                    minLength={8}
                    maxLength={128}
                    placeholder="choose any random string"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => setSecret(generateSecret())}
                      aria-label="Generate new signing secret"
                      data-testid="webhook-generate-secret"
                      title="Generate new signing secret"
                    >
                      <RotateCwIcon />
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
                <FieldDescription>
                  Sent in the <span className="font-mono">X-Mushi-Signature</span> header on every dispatch. The button on the right rolls a fresh secret. Leave the value untouched when editing to keep the existing one.
                </FieldDescription>
              </Field>

              <Field orientation="horizontal" className={cn("items-center justify-between rounded-lg border border-border bg-card px-3 py-2")}>
                <div className="flex flex-col">
                  <FieldLabel htmlFor="webhook-enabled" className="text-sm">Enabled</FieldLabel>
                  <span className="text-xs text-muted-foreground">
                    Disabled webhooks are kept in the list but never receive events.
                  </span>
                </div>
                <input
                  id="webhook-enabled"
                  type="checkbox"
                  className="size-5 rounded border-border accent-foreground"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  data-testid="webhook-enabled"
                  aria-label="Webhook enabled"
                />
              </Field>
            </FieldGroup>

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        )}

        <DialogFooter className="mt-2">
          {mintedSecret ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="webhook-form"
                disabled={busy}
                data-testid="webhook-submit"
              >
                {busy ? "Saving…" : isEdit ? "Save changes" : "Create webhook"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// generateSecret returns 24 random hex chars that are easy to copy and
// unique enough to act as an HMAC secret. crypto.getRandomValues is the
// only source we trust for this.
export function generateSecret(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// The Type field is fixed in the UI for now; expose it as a typed
// re-export so pages can reference it without re-importing from lib/api.
export type { WebhookType };
